import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import {
  getPublicationDisplayName,
  getScenarioDisplayName,
} from "./laborModelPublicationStatus";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 760,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "82vh",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: 24,
  padding: "24px",
  overflowY: "auto",
};

const changeClasses = {
  added: "bg-emerald-100 text-emerald-800",
  removed: "bg-rose-100 text-rose-800",
  modified: "bg-amber-100 text-amber-800",
};

function LaborModelScenarioCompareModal({
  open,
  onClose,
  scenario,
  publication,
  activePublication,
  onLoad,
  comparisonResult,
}) {
  const [result, setResult] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const requestRef = React.useRef(0);
  React.useEffect(() => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    if (!open) {
      setResult(null);
      setError("");
      return;
    }
    if (publication) {
      setResult(comparisonResult || { changes: [], change_count: 0 });
      setLoading(false);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    onLoad("active", null)
      .then((response) => {
        if (requestRef.current === requestId) {
          setResult(response);
        }
      })
      .catch((requestError) => {
        if (requestRef.current !== requestId) return;
        setResult(null);
        setError(
          requestError?.response?.data?.system?.message ||
            "Unable to compare these labor models. Please try again."
        );
      })
      .finally(() => {
        if (requestRef.current === requestId) {
          setLoading(false);
        }
      });
  }, [comparisonResult, open, onLoad, publication]);

  const modelName = publication
    ? getPublicationDisplayName(publication)
    : getScenarioDisplayName(scenario);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <CompareArrowsIcon className="text-violet-700" />
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                Changes in {modelName}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Everything that differs from the live published model.
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close comparison"
            onClick={onClose}
            className="rounded text-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Compared with
          </p>
          <p className="mt-1 text-sm font-medium text-slate-800">
            {getPublicationDisplayName(activePublication)}
          </p>
        </div>

        <div className="mt-6 border-t border-slate-200 pt-5">
          {loading ? (
            <p className="py-10 text-center text-sm text-slate-500">Comparing…</p>
          ) : error ? (
            <div className="rounded-xl bg-rose-50 p-5 text-sm text-rose-800">
              {error}
            </div>
          ) : (result?.changes || []).length === 0 ? (
            <div className="rounded-xl bg-emerald-50 p-5 text-sm text-emerald-800">
              No model differences detected.
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm font-medium text-slate-700">
                {result.change_count} difference
                {result.change_count === 1 ? "" : "s"} from{" "}
                the live published model
              </p>
              {result.changes.map((change, index) => (
                <div key={`${change.title}-${index}`} className="border-b border-slate-100 pb-4">
                  <div className="flex items-start gap-2">
                    <span
                      className={`mt-0.5 rounded px-2 py-0.5 text-[10px] font-semibold uppercase ${
                        changeClasses[change.change_type] || changeClasses.modified
                      }`}
                    >
                      {change.change_type}
                    </span>
                    <span className="text-sm font-medium text-slate-900">
                      {change.title}
                    </span>
                  </div>
                  {(change.details || []).length > 0 && (
                    <ul className="ml-16 mt-2 list-disc space-y-1 pl-4 text-xs text-slate-600">
                      {change.details.map((detail) => (
                        <li key={detail}>{detail}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Box>
    </Modal>
  );
}

export default LaborModelScenarioCompareModal;
